import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ActionBanner from '../components/ActionBanner';
import {
  getClientProfile,
  listDAppConfigs,
  listPolicies,
  listMappings,
  ClientProfile,
  DAppConfig,
  MappingRecord,
  PolicyRecord,
} from '../services/trustline';
import { getNetworkForDApp } from '../config/networks';
import { byModifiedThenName, formatDate, latest, NO_DATE_HINT } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';
import { extractClientProfile, getMissingClientProfileFields } from '../utils/clientProfile';
import { engineTypeBadgeClass } from '../utils/engineBadges';
import DashIconButton from '../components/DashIconButton';
import ContractAddress from '../components/ContractAddress';
import CopyableId from '../components/CopyableId';
import '../components/layout/DashboardLayout.css';

const OverviewPage: React.FC = () => {
  const { clientId } = useAuth();
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [showProfileNudge, setShowProfileNudge] = useState(false);
  const [dApps, setDApps] = useState<DAppConfig[]>([]);
  const [policies, setPolicies] = useState<PolicyRecord[]>([]);
  const [mappings, setMappings] = useState<MappingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const [profileRes, dAppsRes, policiesRes, mappingsRes] = await Promise.all([
        getClientProfile(),
        listDAppConfigs(),
        listPolicies(),
        listMappings(),
      ]);
      setMappings(mappingsRes.success && mappingsRes.data?.mappings ? mappingsRes.data.mappings : []);
      if (cancelled) return;

      if (!profileRes.success && !dAppsRes.success && !policiesRes.success) {
        setError(profileRes.error || dAppsRes.error || policiesRes.error || 'Failed to load');
      }

      const extracted = profileRes.success ? extractClientProfile(profileRes.data) : null;
      setProfile(extracted);
      setShowProfileNudge(getMissingClientProfileFields(extracted).length > 0);

      if (dAppsRes.success && dAppsRes.data?.dAppConfigs) {
        setDApps(dAppsRes.data.dAppConfigs);
      }
      if (policiesRes.success && policiesRes.data?.policies) {
        setPolicies(policiesRes.data.policies);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className="loading">Loading overview…</div>;
  }
  const modifiedAt = (d: DAppConfig) =>
    latest(d.updatedAt, d.createdAt, ...mappings.filter((m) => m.dAppConfigId === d.id).map((m) => m.updatedAt ?? m.createdAt));
  // Most recently changed first, then by name.
  const recent = [...dApps].sort(byModifiedThenName(modifiedAt, (d) => d.name ?? '')).slice(0, 5);

  const firstName = profile?.firstName?.trim() || '';

  return (
    <div>
      <div className="dash-page-header">
        <h2>Overview</h2>
        <p>Welcome back{firstName ? `, ${firstName}` : ''}.</p>
      </div>

      {error && <div className="dash-error">{error}</div>}

      <div className="dash-grid" style={{ marginBottom: '1.25rem' }}>
        <div className="dash-stat">
          <div className="dash-stat-label">dApps</div>
          <div className="dash-stat-value">{dApps.length}</div>
        </div>
        <div className="dash-stat">
          <div className="dash-stat-label">Policies</div>
          <div className="dash-stat-value">{policies.length}</div>
        </div>
        <div className="dash-stat">
          <div className="dash-stat-label">Client ID</div>
          <div className="dash-stat-value dash-stat-value-id">
            <CopyableId
              value={clientId || profile?.clientId || ''}
              label="Client ID"
              truncate={false}
            />
          </div>
        </div>
      </div>

      {showProfileNudge && (
        <div className="dash-nudge" role="status">
          <div className="dash-nudge-body">
            <h3 className="dash-nudge-title">Client information required</h3>
            <p className="dash-nudge-text">
              Your profile is incomplete. Please add your client type, first name, last name, and
              address before continuing.
            </p>
          </div>
          <Link to="/client" className="dash-btn dash-btn-primary">
            Add client info
          </Link>
        </div>
      )}

      <ActionBanner
        title="Protect a new smart contract"
        text="Onboard a dApp: register its contract, then choose which methods Trustline checks and with which policy."
        label="Onboard a new dApp"
        to="/dapps/new"
      />
      <div className="dash-card">
        <h3 style={{ marginBottom: '0.75rem' }}>Recent Activity</h3>
        {dApps.length === 0 ? (
          <p className="dash-empty">No dApps yet: the dApps you onboard and their changes will show here.</p>
        ) : (
          <>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Last modified</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Chain</th>
                    <th>Contract</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((d) => (
                    <tr key={d.id}>
                      <td className="dash-actions-cell">
                        <DashIconButton
                          action="manage"
                          title={`Manage ${d.name}`}
                          to={`/dapps/${encodeURIComponent(d.chainId)}/${encodeURIComponent(d.contractAddress)}`}
                        />
                      </td>
                      <td title={modifiedAt(d) ? undefined : NO_DATE_HINT}>{formatDate(modifiedAt(d))}</td>
                      <td>{d.name}</td>
                      <td>
                        <span className={engineTypeBadgeClass(d.type)}>{d.type}</span>
                      </td>
                      <td>{getNetworkForDApp(d.chainId, d.type)?.name ?? d.chainId}</td>
                      <td>
                        <ContractAddress address={d.contractAddress} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {dApps.length > 5 && (
              <p style={{ marginTop: '0.75rem' }}>
                <Link to="/dapps" className="dash-link">
                  View all dApps →
                </Link>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OverviewPage;
